import { useEffect, useState, useRef, useCallback } from 'react';
import { INACTIVITY_CONFIG } from './inactivity-detection.constants';

export interface UseInactivityDetectionOptions {
  warningTimeoutMs?: number;
  logoutTimeoutMs?: number;
  enabled?: boolean;
}

export interface UseInactivityDetectionReturn {
  isWarningVisible: boolean;
  timeUntilLogout: number;
  isTimedOut: boolean;
  resetTimers: () => void;
}

type InactivityMessage =
  | { type: 'activity'; timestamp: number }
  | { type: 'warning-dismissed'; timestamp: number }
  | { type: 'logout'; reason: 'timeout' | 'user-requested' };

export default function useInactivityDetection(
  options: UseInactivityDetectionOptions = {}
): UseInactivityDetectionReturn {
  const {
    warningTimeoutMs = INACTIVITY_CONFIG.WARNING_TIMEOUT_MS,
    logoutTimeoutMs = INACTIVITY_CONFIG.LOGOUT_TIMEOUT_MS,
    enabled = true,
  } = options;

  const [lastActivityTimestamp, setLastActivityTimestamp] = useState<number>(
    () => {
      const stored = localStorage.getItem(INACTIVITY_CONFIG.LAST_ACTIVITY_KEY);

      return stored ? parseInt(stored, 10) : Date.now();
    }
  );
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [timeUntilLogout, setTimeUntilLogout] = useState(0);
  const [isTimedOut, setIsTimedOut] = useState(false);

  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  /**
   * Update activity timestamp and persist to localStorage
   */
  const updateActivity = useCallback((timestamp: number) => {
    setLastActivityTimestamp(timestamp);
    localStorage.setItem(
      INACTIVITY_CONFIG.LAST_ACTIVITY_KEY,
      timestamp.toString()
    );

    setIsWarningVisible(false);
    setTimeUntilLogout(0);
  }, []);

  /**
   * Handle activity events with debouncing
   */
  const handleActivity = useCallback(() => {
    if (!enabled || isWarningVisible) {
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      const now = Date.now();
      updateActivity(now);

      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'activity',
          timestamp: now,
        } as InactivityMessage);
      }
    }, INACTIVITY_CONFIG.ACTIVITY_DEBOUNCE_MS);
  }, [enabled, isWarningVisible, updateActivity]);

  /**
   * Reset all timers and restart the inactivity detection cycle
   */
  const resetTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    const now = Date.now();
    setLastActivityTimestamp(now);
    localStorage.setItem(INACTIVITY_CONFIG.LAST_ACTIVITY_KEY, now.toString());

    setIsWarningVisible(false);
    setTimeUntilLogout(0);
    setIsTimedOut(false);

    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: 'activity',
        timestamp: now,
      } as InactivityMessage);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    const startCountdown = () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      countdownIntervalRef.current = setInterval(() => {
        const currentTime = Date.now();
        const elapsed = currentTime - lastActivityTimestamp;
        const remaining = logoutTimeoutMs - elapsed;

        if (remaining <= 0) {
          setTimeUntilLogout(0);
          setIsTimedOut(true);

          if (broadcastChannelRef.current) {
            broadcastChannelRef.current.postMessage({
              type: 'logout',
              reason: 'timeout',
            } as InactivityMessage);
          }

          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
        } else {
          setTimeUntilLogout(remaining);
        }
      }, INACTIVITY_CONFIG.COUNTDOWN_INTERVAL_MS);
    };

    const setLogoutTimer = (delayMs: number) => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }

      logoutTimerRef.current = setTimeout(() => {
        setIsTimedOut(true);

        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'logout',
            reason: 'timeout',
          } as InactivityMessage);
        }
      }, delayMs);
    };

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityTimestamp;

    if (timeSinceLastActivity >= warningTimeoutMs) {
      setIsWarningVisible(true);
      startCountdown();
      const timeUntilLogoutMs = logoutTimeoutMs - timeSinceLastActivity;
      setLogoutTimer(timeUntilLogoutMs);
    } else {
      const timeUntilWarningMs = warningTimeoutMs - timeSinceLastActivity;

      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }

      warningTimerRef.current = setTimeout(() => {
        setIsWarningVisible(true);
        startCountdown();
        const timeUntilLogoutMs =
          logoutTimeoutMs - (Date.now() - lastActivityTimestamp);
        setLogoutTimer(timeUntilLogoutMs);
      }, timeUntilWarningMs);
    }

    return () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }

      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }

      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [enabled, lastActivityTimestamp, warningTimeoutMs, logoutTimeoutMs]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    broadcastChannelRef.current = new BroadcastChannel(
      INACTIVITY_CONFIG.BROADCAST_CHANNEL_NAME
    );

    broadcastChannelRef.current.onmessage = (
      event: MessageEvent<InactivityMessage>
    ) => {
      const message = event.data;

      if (message.type === 'activity') {
        updateActivity(message.timestamp);
      } else if (message.type === 'warning-dismissed') {
        setIsWarningVisible(false);
        setTimeUntilLogout(0);
        updateActivity(message.timestamp);
      } else if (message.type === 'logout') {
        setIsTimedOut(true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const stored = localStorage.getItem(
          INACTIVITY_CONFIG.LAST_ACTIVITY_KEY
        );
        const lastActivity = stored ? parseInt(stored, 10) : now;
        const timeSinceLastActivity = now - lastActivity;

        if (timeSinceLastActivity >= logoutTimeoutMs) {
          setIsTimedOut(true);

          if (broadcastChannelRef.current) {
            broadcastChannelRef.current.postMessage({
              type: 'logout',
              reason: 'timeout',
            } as InactivityMessage);
          }
        } else if (
          timeSinceLastActivity >= warningTimeoutMs &&
          !isWarningVisible
        ) {
          setIsWarningVisible(true);
          setTimeUntilLogout(logoutTimeoutMs - timeSinceLastActivity);
        }
      }
    };

    visibilityHandlerRef.current = handleVisibilityChange;
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const PASSIVE_EVENTS = ['scroll', 'touchstart', 'pointermove'];

    INACTIVITY_CONFIG.ACTIVITY_EVENTS.forEach(eventType => {
      const usePassive = PASSIVE_EVENTS.includes(eventType);
      document.addEventListener(
        eventType,
        handleActivity,
        usePassive ? { passive: true } : undefined
      );
    });

    return () => {
      INACTIVITY_CONFIG.ACTIVITY_EVENTS.forEach(eventType => {
        document.removeEventListener(eventType, handleActivity);
      });

      if (visibilityHandlerRef.current) {
        document.removeEventListener(
          'visibilitychange',
          visibilityHandlerRef.current
        );
      }

      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    enabled,
    handleActivity,
    updateActivity,
    warningTimeoutMs,
    logoutTimeoutMs,
    isWarningVisible,
  ]);

  return {
    isWarningVisible,
    timeUntilLogout,
    isTimedOut,
    resetTimers,
  };
}
